import React from "react";
import { Link } from "react-router-dom";
import PageHero from "@/components/site/PageHero";
import Reveal from "@/components/site/Reveal";
import SEO from "@/components/site/SEO";
import WavyDivider from "@/components/site/WavyDivider";
import { DecorationField, Star } from "@/components/site/Decorations";
import { Button } from "@/components/ui/button";
import {
  Smile,
  Blocks,
  Palette,
  BookOpen,
  FlaskConical,
  Puzzle,
  Leaf,
  Music,
  Clock,
  Sun,
  Shapes,
  UtensilsCrossed,
  Backpack,
  Hand,
  Sparkles,
  PartyPopper,
  Info,
  Cake,
  MapPin,
  HeartHandshake,
  Languages,
  Home,
  ExternalLink,
  ArrowRight,
} from "lucide-react";

const FINGER_PAINTING_IMG =
  "https://customer-assets.emergentagent.com/job_early-learning-hub-28/artifacts/qum8z94q_looking-down-at-toddler-finger-painting-2022-11-15-13-03-47-utc.jpg";

const LEARNING_AREAS = [
  { icon: Smile, label: "Dramatic Play" },
  { icon: Blocks, label: "Blocks & Construction" },
  { icon: Palette, label: "Art" },
  { icon: BookOpen, label: "Books & Literacy" },
  { icon: FlaskConical, label: "Discovery & Science" },
  { icon: Puzzle, label: "Manipulatives" },
  { icon: Leaf, label: "Nature Exploration" },
  { icon: Music, label: "Music & Movement" },
];

const DAY_RHYTHM = [
  "Breakfast",
  "Classroom exploration & learning centers",
  "Group experiences",
  "Outdoor play",
  "Lunch",
  "Rest time",
  "Enrichment activities",
  "Afternoon snack",
  "Stories, music & movement",
  "Reflecting on the day",
];

const BEYOND_CLASSROOM = [
  {
    icon: Sun,
    color: "from-amber-500 to-amber-600",
    title: "Outdoor Playground",
    desc: "Children can enjoy our outdoor playground, with space to run, climb, and play together.",
  },
  {
    icon: BookOpen,
    color: "from-brand-purple to-brand-purple-deep",
    title: "Book of the Month",
    desc: "Children participate in our Book of the Month experiences, building an early love of stories.",
  },
  {
    icon: Shapes,
    color: "from-brand-green to-brand-green-deep",
    title: "Loose Parts Initiative",
    desc: "Simple, open-ended materials become whatever a child's imagination can create.",
  },
];

const INDEPENDENCE_WINS = [
  { icon: Backpack, label: "Hanging up a backpack" },
  { icon: UtensilsCrossed, label: "Sitting with friends for breakfast" },
  { icon: Hand, label: "Asking a teacher for help" },
  { icon: Star, label: "Choosing an activity" },
  { icon: Sparkles, label: "Cleaning up a toy" },
  { icon: PartyPopper, label: 'Proudly saying, "I did it!"' },
];

const ELIGIBILITY = [
  { icon: Cake, label: "Born in 2024" },
  { icon: MapPin, label: "Live in New York City" },
  { icon: HeartHandshake, label: "Children with disabilities & accessibility needs welcome" },
  { icon: Languages, label: "Children learning English welcome" },
  { icon: Home, label: "Children in temporary housing welcome" },
];

const MYSCHOOLS_STEPS = [
  "Create your family's account",
  "Add your child",
  "Explore available 2-K programs",
  "Select the programs you're interested in",
  "Submit an application",
  "Manage offers and waitlists",
];

export default function TwoKPage() {
  return (
    <div data-testid="two-k-page" className="overflow-hidden">
      <SEO
        title="2K Program"
        description="Discover 2K at Innovators Prep — a joyful first step into school for two-year-olds in Brooklyn, NY. Free through NYC Public Schools' 2-K initiative. Apply through MySchools."
      />
      <PageHero
        title="Discover 2K at Innovators Prep"
        crumbs={[
          { label: "Preschool Program", to: "/programs/preschool" },
          { label: "2K" },
        ]}
      />

      {/* Intro */}
      <section className="relative pt-20 md:pt-28 pb-28 md:pb-36" data-testid="two-k-intro">
        <DecorationField variant="mixed" />
        <div className="relative max-w-7xl mx-auto px-6 md:px-10 grid lg:grid-cols-2 gap-14 items-center">
          <Reveal>
            <span className="inline-block px-3 py-1 rounded-full bg-brand-green-soft text-brand-green-deep text-xs font-bold tracking-widest uppercase">
              Welcome to 2K
            </span>
            <h2 className="mt-3 font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 tracking-tight">
              A joyful first step <span className="text-brand-green">into school</span>
            </h2>
            <div className="mt-5 space-y-4 text-gray-600 text-lg leading-relaxed">
              <p>
                Choosing your child's first school is a big milestone — for your child and for you! At
                Innovators Prep, we want that first experience to feel exciting, comfortable, and full of
                possibility. Our 2K Program is designed especially for curious, active two-year-olds who are
                ready to begin exploring the world beyond home.
              </p>
              <p>
                In a warm and nurturing preschool environment, children have opportunities to make friends,
                develop independence, strengthen communication skills, and discover just how much fun
                learning can be.
              </p>
            </div>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="relative">
              <div className="absolute -inset-6 bg-brand-green-soft rounded-[3rem] -rotate-2" />
              <img
                src={FINGER_PAINTING_IMG}
                alt="Toddler finger painting during a classroom art activity"
                className="relative rounded-[2.5rem] shadow-xl object-cover w-full aspect-[4/3]"
              />
              <div className="absolute -top-4 -right-3">
                <Star color="#FBBF24" size={40} />
              </div>
            </div>
          </Reveal>
        </div>
        <WavyDivider fill="#FFFFFF" />
      </section>

      {/* A Place to Play, Explore & Grow */}
      <section className="relative bg-white pt-20 md:pt-28 pb-28 md:pb-36" data-testid="two-k-play">
        <div className="relative max-w-7xl mx-auto px-6 md:px-10">
          <Reveal className="max-w-3xl">
            <span className="inline-block px-3 py-1 rounded-full bg-brand-purple-soft text-brand-purple-deep text-xs font-bold tracking-widest uppercase">
              Learning Through Play
            </span>
            <h2 className="mt-3 font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 tracking-tight">
              A Place to Play, Explore & <span className="text-brand-purple">Grow</span>
            </h2>
          </Reveal>
          <div className="mt-8 grid lg:grid-cols-2 gap-10 items-start">
            <Reveal>
              <div className="space-y-4 text-gray-600 text-lg leading-relaxed">
                <p>
                  At two years old, children learn best by doing. They want to touch, build, move, pretend,
                  create, investigate, and ask questions — and our program embraces all of it! Our classrooms
                  are thoughtfully designed around learning through play.
                </p>
                <p>
                  These everyday experiences help children develop important early skills while still
                  allowing them to enjoy what two-year-olds do best: play! Teachers are there to encourage
                  children, introduce new ideas, model language, support friendships, and help children
                  gradually become more confident and independent.
                </p>
                <p className="font-semibold text-gray-800">
                  We recognize that every two-year-old develops at their own pace, so our goal isn't to rush
                  childhood — it's to give children a strong, happy beginning.
                </p>
              </div>
            </Reveal>
            <Reveal delay={0.1}>
              <div className="rounded-[2.5rem] bg-brand-cream border border-gray-100 shadow-md p-7 md:p-8">
                <p className="font-display text-lg font-bold text-gray-900">
                  Throughout the day, children explore areas for:
                </p>
                <div className="mt-5 grid sm:grid-cols-2 gap-3">
                  {LEARNING_AREAS.map((area) => (
                    <div
                      key={area.label}
                      className="flex items-center gap-3 bg-white rounded-2xl px-4 py-3 border border-gray-100 shadow-sm"
                    >
                      <span className="h-9 w-9 shrink-0 rounded-xl bg-brand-green-soft text-brand-green-deep flex items-center justify-center">
                        <area.icon className="h-5 w-5" />
                      </span>
                      <span className="text-[15px] font-semibold text-gray-800">{area.label}</span>
                    </div>
                  ))}
                </div>
              </div>
            </Reveal>
          </div>
        </div>
        <WavyDivider fill="#FFFCF9" />
      </section>

      {/* What Does a Day at 2K Look Like? */}
      <section className="relative pt-20 md:pt-28 pb-28 md:pb-36" data-testid="two-k-day">
        <DecorationField variant="mixed" />
        <div className="relative max-w-7xl mx-auto px-6 md:px-10">
          <Reveal className="max-w-3xl">
            <span className="inline-block px-3 py-1 rounded-full bg-brand-green-soft text-brand-green-deep text-xs font-bold tracking-widest uppercase">
              The Daily Routine
            </span>
            <h2 className="mt-3 font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 tracking-tight">
              What Does a Day at 2K <span className="text-brand-green">Look Like?</span>
            </h2>
          </Reveal>

          <div className="mt-10 grid lg:grid-cols-5 gap-8 items-start">
            <Reveal className="lg:col-span-3">
              <div className="space-y-4 text-gray-600 text-lg leading-relaxed">
                <p>
                  Our 2K school day runs from{" "}
                  <span className="font-bold text-gray-900">8:00 AM to 2:20 PM</span>. Children follow a
                  predictable daily routine that helps them become comfortable with school while providing
                  plenty of variety throughout the day.
                </p>
                <p>
                  Breakfast, lunch, and an afternoon snack are provided, making mornings a little easier for
                  busy families. And learning isn't limited to the classroom.
                </p>
                <p>
                  Our Administration and Educators are always exploring new ideas to develop learning
                  opportunities for our 2K. We are just getting started!
                </p>
              </div>
              <div className="mt-7 flex flex-wrap gap-2.5">
                {DAY_RHYTHM.map((item) => (
                  <span
                    key={item}
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white border border-gray-100 shadow-sm text-sm font-semibold text-gray-700"
                  >
                    <span className="h-2 w-2 rounded-full bg-brand-green" />
                    {item}
                  </span>
                ))}
              </div>
            </Reveal>
            <Reveal delay={0.1} className="lg:col-span-2">
              <div className="rounded-[2rem] p-7 bg-gradient-to-br from-brand-green to-brand-green-deep text-white shadow-lg relative overflow-hidden">
                <div aria-hidden className="absolute -top-8 -right-8 opacity-15">
                  <Clock size={120} color="#fff" />
                </div>
                <span className="inline-block text-[10px] font-bold tracking-widest uppercase px-2.5 py-1 rounded-full bg-white/25">
                  School Hours
                </span>
                <div className="mt-3 font-display text-3xl md:text-4xl font-bold leading-tight">
                  8:00 AM — 2:20 PM
                </div>
                <p className="mt-3 text-white/90 text-[15px] leading-relaxed">
                  A predictable daily routine with breakfast, lunch, and an afternoon snack all provided.
                </p>
              </div>
            </Reveal>
          </div>

          <div className="mt-10 grid sm:grid-cols-3 gap-6">
            {BEYOND_CLASSROOM.map((card, i) => (
              <Reveal key={card.title} delay={i * 0.08}>
                <div className="h-full bg-white rounded-[2rem] border border-gray-100 shadow-sm hover:shadow-md transition-shadow p-6">
                  <span
                    className={`h-12 w-12 rounded-2xl bg-gradient-to-br ${card.color} text-white flex items-center justify-center shadow-md`}
                  >
                    <card.icon className="h-6 w-6" />
                  </span>
                  <h3 className="mt-4 font-display text-xl font-bold text-gray-900">{card.title}</h3>
                  <p className="mt-2 text-[15px] text-gray-600 leading-relaxed">{card.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
        <WavyDivider fill="#FFFFFF" />
      </section>

      {/* Independence */}
      <section className="relative bg-white pt-20 md:pt-28 pb-28 md:pb-36" data-testid="two-k-independence">
        <div className="relative max-w-7xl mx-auto px-6 md:px-10">
          <Reveal className="max-w-3xl">
            <span className="inline-block px-3 py-1 rounded-full bg-brand-purple-soft text-brand-purple-deep text-xs font-bold tracking-widest uppercase">
              Growing Confidence
            </span>
            <h2 className="mt-3 font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 tracking-tight">
              Your Child Is Becoming More Independent —{" "}
              <span className="text-brand-purple">And We'll Help</span>
            </h2>
            <p className="mt-5 text-gray-600 text-lg leading-relaxed">
              For many children, 2K is their very first experience being part of a classroom community. That
              means some of the biggest accomplishments may look surprisingly small. Those moments matter.
              Our teachers help children practice age-appropriate routines, communication, problem-solving,
              cooperation, self-help skills, and emotional development throughout the school day.
            </p>
          </Reveal>

          <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {INDEPENDENCE_WINS.map((win, i) => (
              <Reveal key={win.label} delay={i * 0.05}>
                <div className="flex items-center gap-3 bg-brand-cream rounded-2xl px-5 py-4 border border-gray-100 h-full">
                  <span className="h-10 w-10 shrink-0 rounded-xl bg-white shadow-sm text-brand-purple-deep flex items-center justify-center">
                    <win.icon className="h-5 w-5" />
                  </span>
                  <span className="text-[15px] font-semibold text-gray-800">{win.label}</span>
                </div>
              </Reveal>
            ))}
          </div>

          <Reveal>
            <div
              data-testid="toilet-training-note"
              className="mt-8 rounded-[2rem] bg-brand-green-soft border border-brand-green/20 p-6 md:p-7 flex items-start gap-4"
            >
              <span className="h-11 w-11 shrink-0 rounded-xl bg-brand-green text-white flex items-center justify-center shadow-md">
                <Info className="h-5 w-5" />
              </span>
              <p className="text-[15.5px] text-gray-800 leading-relaxed">
                <span className="font-bold text-brand-green-deep">
                  And if your child is not toilet trained yet, that's okay.
                </span>{" "}
                Children do not need to be toilet trained to participate in NYC's 2-K program.
              </p>
            </div>
          </Reveal>
        </div>
        <WavyDivider fill="#FFFCF9" />
      </section>

      {/* Families */}
      <section className="relative pt-20 md:pt-28 pb-28 md:pb-36" data-testid="two-k-families">
        <DecorationField variant="mixed" />
        <div className="relative max-w-4xl mx-auto px-6 md:px-10 text-center">
          <Reveal>
            <span className="inline-block px-3 py-1 rounded-full bg-brand-green-soft text-brand-green-deep text-xs font-bold tracking-widest uppercase">
              Partnership
            </span>
            <h2 className="mt-3 font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 tracking-tight">
              Families Are Part of Our <span className="text-brand-green">School Community, Too!</span>
            </h2>
            <div className="mt-5 space-y-4 text-gray-600 text-lg leading-relaxed text-left sm:text-center">
              <p>
                We believe children do best when families and teachers work together. You know your child
                better than anyone, and we want to know what makes them happy, curious, comfortable, excited
                — or sometimes a little unsure. Sharing information about your child's interests, routines,
                strengths, communication, and needs helps our teachers get to know the whole child.
              </p>
              <p>
                We encourage open communication and want families to feel comfortable asking questions,
                sharing concerns, celebrating milestones, and participating in their child's school
                experience.
              </p>
            </div>
          </Reveal>
        </div>
        <WavyDivider fill="#FFFFFF" />
      </section>

      {/* Who Can Apply */}
      <section className="relative bg-white pt-20 md:pt-28 pb-28 md:pb-36" data-testid="two-k-eligibility">
        <div className="relative max-w-7xl mx-auto px-6 md:px-10 grid lg:grid-cols-2 gap-12 items-start">
          <Reveal>
            <span className="inline-block px-3 py-1 rounded-full bg-brand-purple-soft text-brand-purple-deep text-xs font-bold tracking-widest uppercase">
              Eligibility
            </span>
            <h2 className="mt-3 font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 tracking-tight">
              Who Can Apply for <span className="text-brand-purple">NYC 2-K?</span>
            </h2>
            <div className="mt-5 space-y-4 text-gray-600 text-lg leading-relaxed">
              <p>
                Innovators Prep participates in NYC Public Schools' 2-K initiative, which provides{" "}
                <span className="font-bold text-gray-900">free early care and education</span> opportunities
                for eligible two-year-old children.
              </p>
              <p>
                For Fall 2026, children born in 2024 who live in New York City are eligible to apply.
                Children generally must be two years old before beginning the program. A child who turns two
                later in the calendar year may receive a pending offer with a later start date.
              </p>
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="rounded-[2.5rem] bg-brand-cream border border-gray-100 shadow-md p-7 md:p-8">
              <p className="font-display text-lg font-bold text-gray-900">The program welcomes:</p>
              <ul className="mt-5 space-y-3">
                {ELIGIBILITY.map((item) => (
                  <li
                    key={item.label}
                    className="flex items-center gap-3 bg-white rounded-2xl px-4 py-3 border border-gray-100 shadow-sm"
                  >
                    <span className="h-9 w-9 shrink-0 rounded-xl bg-brand-purple-soft text-brand-purple-deep flex items-center justify-center">
                      <item.icon className="h-5 w-5" />
                    </span>
                    <span className="text-[15px] font-semibold text-gray-800">{item.label}</span>
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>
        </div>
        <WavyDivider fill="#FFFCF9" />
      </section>

      {/* Apply Through MySchools */}
      <section className="relative pt-20 md:pt-28 pb-28 md:pb-36" data-testid="two-k-apply">
        <DecorationField variant="mixed" />
        <div className="relative max-w-7xl mx-auto px-6 md:px-10">
          <Reveal className="text-center max-w-3xl mx-auto">
            <span className="inline-block px-3 py-1 rounded-full bg-brand-green-soft text-brand-green-deep text-xs font-bold tracking-widest uppercase">
              Enrollment
            </span>
            <h2 className="mt-3 font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 tracking-tight">
              Interested in Innovators Prep?{" "}
              <span className="text-brand-green">Apply Through MySchools</span>
            </h2>
            <p className="mt-5 text-gray-600 text-lg leading-relaxed">
              Families apply for NYC 2-K through MySchools, the NYC Public Schools admissions system. Through
              MySchools, you can:
            </p>
          </Reveal>

          <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-4 max-w-5xl mx-auto">
            {MYSCHOOLS_STEPS.map((step, i) => (
              <Reveal key={step} delay={i * 0.05}>
                <div className="flex items-center gap-4 bg-white rounded-2xl px-5 py-4 border border-gray-100 shadow-sm h-full">
                  <span className="h-9 w-9 shrink-0 rounded-full bg-gradient-to-br from-brand-green to-brand-green-deep text-white font-display font-bold text-sm flex items-center justify-center shadow-md">
                    {i + 1}
                  </span>
                  <span className="text-[15px] font-semibold text-gray-800">{step}</span>
                </div>
              </Reveal>
            ))}
          </div>

          <Reveal>
            <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4">
              <Button
                asChild
                data-testid="myschools-apply-button"
                className="bg-brand-green hover:bg-brand-green-deep text-white rounded-full font-semibold px-7 py-6 shadow-md"
              >
                <a href="https://www.myschools.nyc/" target="_blank" rel="noopener noreferrer">
                  Explore 2-K and apply through MySchools <ExternalLink className="h-4 w-4 ml-1" />
                </a>
              </Button>
              <Button
                asChild
                variant="outline"
                data-testid="nyc-admissions-button"
                className="rounded-full font-semibold px-7 py-6 border-2 border-brand-purple text-brand-purple-deep hover:bg-brand-purple-soft"
              >
                <a
                  href="https://www.schools.nyc.gov/enrollment/enroll-grade-by-grade/2-k"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Learn more about NYC Public Schools 2-K admissions{" "}
                  <ExternalLink className="h-4 w-4 ml-1" />
                </a>
              </Button>
            </div>
          </Reveal>

          <Reveal>
            <p className="mt-8 max-w-3xl mx-auto text-center text-[15px] text-gray-600 leading-relaxed">
              The main Fall 2026 2-K application period has closed, but families who are still looking for a
              2-K placement may join program waitlists through MySchools. A waitlist does not guarantee an
              offer, so families interested in Innovators Prep are encouraged to explore their options as
              soon as possible.
            </p>
          </Reveal>
        </div>
        <WavyDivider fill="#FFFFFF" />
      </section>

      {/* Closing CTA */}
      <section className="relative bg-white py-20 md:py-28" data-testid="two-k-closing">
        <div className="relative max-w-5xl mx-auto px-6 md:px-10">
          <Reveal>
            <div className="rounded-[2.5rem] bg-gradient-to-br from-brand-green to-brand-green-deep text-white p-8 md:p-14 shadow-xl relative overflow-hidden text-center">
              <div aria-hidden className="absolute -top-12 -left-12 h-44 w-44 rounded-full bg-white/10 blur-2xl" />
              <div aria-hidden className="absolute -bottom-12 -right-12 h-44 w-44 rounded-full bg-white/10 blur-2xl" />
              <div className="relative">
                <span className="inline-block px-3 py-1 rounded-full bg-white/25 text-white text-xs font-bold tracking-widest uppercase">
                  Visit Us
                </span>
                <h2 className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight">
                  Come See What Makes Innovators Prep Special
                </h2>
                <div className="mt-6 space-y-4 text-white/90 text-lg leading-relaxed max-w-3xl mx-auto">
                  <p>
                    We invite families considering 2K to come visit Innovators Prep, see our learning spaces,
                    learn more about our daily routine, and ask us all the questions that come with sending a
                    two-year-old to school for the first time.
                  </p>
                  <p>
                    We want you to picture your child here — not simply attending school, but making friends,
                    gaining confidence, discovering new interests, and coming home excited about what they
                    did that day. Their first school experience should be a happy one. We'd love for it to
                    begin at Innovators Prep.
                  </p>
                  <p className="font-display font-bold text-white text-xl">Play. Learn. Grow.</p>
                </div>
                <Button
                  asChild
                  data-testid="book-visit-button"
                  className="mt-8 bg-white text-brand-green-deep hover:bg-brand-cream rounded-full font-bold px-8 py-6 shadow-md"
                >
                  <Link to="/contact">
                    Book a Visit <ArrowRight className="h-4 w-4 ml-1" />
                  </Link>
                </Button>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
